import React, { useState, useEffect, lazy, Suspense } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import { Users, Calendar, FileText, Banknote, TrendingUp, Clock, AlertCircle, Settings, X, GripVertical, Eye, EyeOff } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

// Lazy load AI Widget to not impact dashboard load time
const AIUsageWidget = lazy(() => import('../components/AIUsageWidget'));
const PendingSignaturesWidget = lazy(() => import('../components/PendingSignaturesWidget'));

const DEFAULT_WIDGETS = {
  stats: { visible: true, order: 0, name: 'Statistics Overview' },
  aiUsage: { visible: true, order: 1, name: 'AI Usage (Owner Only)' },
  quickActions: { visible: true, order: 2, name: 'Quick Actions' },
  recentActivities: { visible: true, order: 3, name: 'Recent Activities' }, // TC-DASH-007
  employeeGrowth: { visible: true, order: 4, name: 'Employee Growth Chart' },
  attendanceChart: { visible: true, order: 5, name: 'Attendance Overview' }, // TC-DASH-009
  leaveDistribution: { visible: true, order: 6, name: 'Leave Distribution' }, // TC-DASH-010
  departmentDist: { visible: true, order: 7, name: 'Department Distribution' },
  holidays: { visible: true, order: 8, name: 'Upcoming Holidays' },
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState(() => {
    const saved = localStorage.getItem('dashboard_widgets');
    // TC-DASH-007: merge saved layout with DEFAULT_WIDGETS so newly added
    // widgets always appear, even for users with stale localStorage.
    if (!saved) return DEFAULT_WIDGETS;
    try {
      const parsed = JSON.parse(saved);
      const merged = { ...DEFAULT_WIDGETS };
      Object.keys(DEFAULT_WIDGETS).forEach((k) => {
        if (parsed && parsed[k]) {
          merged[k] = { ...DEFAULT_WIDGETS[k], ...parsed[k] };
        }
      });
      return merged;
    } catch (e) {
      return DEFAULT_WIDGETS;
    }
  });
  const [publicHolidays, setPublicHolidays] = useState([]);
  // TC-DASH-007, TC-DASH-009, TC-DASH-010: New state for dashboard widgets
  const [recentActivities, setRecentActivities] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [leaveDistribution, setLeaveDistribution] = useState([]);
  // Real per-employee stats for the Employee-view summary cards (TC-LV-026..028 dashboard sync)
  const [empStats, setEmpStats] = useState({
    leave_balance_days: null,
    attendance_days_this_month: null,
    pending_claims: null,
  });
  // Iter88t — Per-leave-type breakdown for the dashboard widget.
  const [leaveBreakdown, setLeaveBreakdown] = useState([]);
  const user = JSON.parse(localStorage.getItem('hrms_user') || sessionStorage.getItem('hrms_user') || '{}');
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    fetchStats();
    fetchPublicHolidays();
    fetchRecentActivities();
    fetchLeaveDistribution();
    fetchEmpStats();
  }, []);

  // Iter94 — Re-fetch attendance chart data whenever `stats` loads or
  // changes, so the Absent count reflects the correct total_employees
  // instead of falling back to 0 on the initial (pre-stats) render.
  useEffect(() => {
    fetchAttendanceData();
  }, [stats?.total_employees]);

  useEffect(() => {
    localStorage.setItem('dashboard_widgets', JSON.stringify(widgetConfig));
  }, [widgetConfig]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // TC-LV-026..028, TC-CLM dashboard: pull live per-employee numbers so the
  // 3 summary cards (Leave Balance / This Month / Pending Claims) are never stale.
  const fetchEmpStats = async () => {
    const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
    if (!user.employee_id) return; // only employee-linked roles have personal stats
    try {
      const [balanceRes, claimsRes, attRes] = await Promise.all([
        axios.get(`${API}/leave/balance`).catch(() => ({ data: [] })),
        axios.get(`${API}/claims`).catch(() => ({ data: [] })),
        axios.get(`${API}/attendance`).catch(() => ({ data: [] })),
      ]);
      // Use Annual Leave specifically (the entitlement most HR systems track on the
      // main dashboard). Fall back to total only if Annual entry is absent.
      // Use explicit undefined check so a real 0 balance still shows '0 Days'.
      const balances = balanceRes.data || [];
      setLeaveBreakdown(balances); // Iter88t — store full per-type breakdown
      const annual = balances.find(
        (b) => b.leave_type_id === 'ANN' || /annual/i.test(b.name || ''),
      );
      const totalBalance = annual !== undefined
        ? Number(annual.balance || 0)
        : balances.reduce((acc, b) => acc + Math.max(0, Number(b.balance || 0)), 0);
      // Iter48 (Shikha 22-Jun-2026) — Pending Claims count must include every
      // claim sitting in the multi-stage approval workflow, not just `pending`.
      // A claim badged "Pending Manager Approval" has DB status `hr_checked`
      // and was previously hidden from this count (matches the Iter45 fix in
      // the Reports module).
      const pendingClaimStatuses = new Set([
        'pending', 'hr_checked', 'submitted', 'under_review', 'in_review',
      ]);
      const pendingClaims = (claimsRes.data || []).filter(
        (c) => c.employee_id === user.employee_id && pendingClaimStatuses.has(c.status),
      ).length;
      const now = new Date();
      const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const attendanceThisMonth = (attRes.data || []).filter(
        (r) =>
          r.employee_id === user.employee_id &&
          (r.date || '').startsWith(monthPrefix) &&
          r.clock_in,
      ).length;
      setEmpStats({
        leave_balance_days: totalBalance,
        attendance_days_this_month: attendanceThisMonth,
        pending_claims: pendingClaims,
      });
    } catch (err) {
      // Non-fatal — cards show '—'
      console.warn('fetchEmpStats failed', err?.message);
    }
  };

  const fetchPublicHolidays = async () => {
    try {
      // Endpoint uses hyphen: /public-holidays. Use current year so the list is relevant.
      const year = new Date().getFullYear();
      const response = await axios.get(`${API}/public-holidays?year=${year}`);
      const today = new Date();
      const upcoming = response.data
        .filter(h => new Date(h.date) >= today)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 5);
      setPublicHolidays(upcoming);
    } catch (error) {
      // Non-fatal — widget falls back to empty state
      console.warn('Holidays fetch skipped:', error?.response?.status || error?.message);
    }
  };

  // TC-DASH-007: Fetch recent activities from dedicated backend endpoint
  // (covers leave, claims, payroll runs, new employees with company filtering).
  const fetchRecentActivities = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/recent-activities?limit=10`);
      if (Array.isArray(res.data) && res.data.length > 0) {
        setRecentActivities(res.data);
        return;
      }
    } catch (error) {
      // Fall through to legacy aggregation
      console.warn('Recent activities endpoint failed, using fallback:', error?.message);
    }

    // Fallback: aggregate locally from leave + claims (legacy path)
    try {
      const [leavesRes, claimsRes] = await Promise.all([
        axios.get(`${API}/leave/applications`).catch(() => ({ data: [] })),
        axios.get(`${API}/claims`).catch(() => ({ data: [] })),
      ]);
      const activities = [
        ...((leavesRes.data || []).slice(0, 5).map((l) => ({
          type: 'leave',
          title: `Leave ${l.status || 'submitted'}`,
          description: `${l.employee_name || 'Employee'} - ${l.leave_type_name || l.leave_type || 'Leave'}`,
          time: l.applied_date || l.created_at || l.start_date,
          status: l.status,
        }))),
        ...((claimsRes.data || []).slice(0, 5).map((c) => ({
          type: 'claim',
          title: `Claim ${c.status || 'submitted'}`,
          description: `${c.employee_name || 'Employee'} - RM ${c.amount || 0}`,
          time: c.submitted_date || c.created_at || c.claim_date,
          status: c.status,
        }))),
      ]
        .sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0))
        .slice(0, 5);
      setRecentActivities(activities);
    } catch (error) {
      console.error('Error fetching activities (fallback):', error);
      setRecentActivities([]);
    }
  };

  // TC-DASH-009: Fetch attendance data for chart
  // Iter94 (Shikha 10-Jul-2026) — Absent count was showing 0 on days
  // where explicit absence records existed. Previous code used only
  // `total_employees - present_count`, which returned 0 when the
  // outer `stats` state wasn't loaded yet AND ignored explicit
  // `status='absent'` records. Now:
  //   • Count explicit `status='absent'` rows
  //   • Add "missing" employees (total_employees - attendance_recorded_count)
  //     ONLY when stats is loaded AND the day is a working day (Mon–Sat).
  //   • Sunday defaults to 0 absents (weekly rest day per Iter90).
  const fetchAttendanceData = async () => {
    try {
      const response = await axios.get(`${API}/attendance`).catch(() => ({ data: [] }));
      const totalEmployees = Number(stats?.total_employees || 0);
      const last7Days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dayStr = date.toISOString().split('T')[0];
        const dayAttendance = response.data.filter(a => a.date?.startsWith(dayStr));
        const presentCount = dayAttendance.filter(a => a.status === 'present' || a.clock_in).length;
        const lateCount = dayAttendance.filter(a => a.status === 'late').length;
        const explicitAbsentCount = dayAttendance.filter(a => a.status === 'absent').length;
        // Anyone with ANY attendance row (present / late / absent / on_leave / early)
        const recordedCount = dayAttendance.filter(a => a.status).length;
        const isSunday = date.getDay() === 0;
        let missingCount = 0;
        if (totalEmployees > 0 && !isSunday) {
          missingCount = Math.max(0, totalEmployees - recordedCount);
        }
        last7Days.push({
          day: date.toLocaleDateString('en-US', { weekday: 'short' }),
          present: presentCount,
          absent: explicitAbsentCount + missingCount,
          late: lateCount,
        });
      }
      setAttendanceData(last7Days);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  // TC-DASH-010: Fetch leave distribution
  const fetchLeaveDistribution = async () => {
    try {
      // Fetch ALL leave applications (no status filter) so the chart sees pending+approved+rejected
      const response = await axios.get(`${API}/leave/applications`).catch(() => ({ data: [] }));
      // Pre-resolve leave type names (the api returns leave_type_id)
      let typeNameById = {};
      try {
        const typesResp = await axios.get(`${API}/leave/types`).catch(() => ({ data: [] }));
        typeNameById = (typesResp.data || []).reduce((acc, t) => {
          acc[t.leave_type_id] = t.name;
          return acc;
        }, {});
      } catch (_) { /* non-fatal */ }
      const distribution = {};
      (response.data || []).forEach(leave => {
        const type = leave.leave_type || typeNameById[leave.leave_type_id] || 'Other';
        distribution[type] = (distribution[type] || 0) + 1;
      });
      const palette = ['#10b981', '#ef4444', '#f59e0b', '#6366f1', '#0ea5e9', '#a855f7'];
      const chartData = Object.entries(distribution).map(([name, value], idx) => ({
        name,
        value,
        color: palette[idx % palette.length],
      }));
      setLeaveDistribution(chartData);
    } catch (error) {
      console.error('Error fetching leave distribution:', error);
    }
  };

  const toggleWidget = (widgetId) => {
    setWidgetConfig(prev => ({
      ...prev,
      [widgetId]: { ...prev[widgetId], visible: !prev[widgetId].visible }
    }));
  };

  const moveWidgetUp = (widgetId) => {
    setWidgetConfig(prev => {
      const widgets = { ...prev };
      const currentOrder = widgets[widgetId].order;
      if (currentOrder > 0) {
        const otherWidget = Object.keys(widgets).find(k => widgets[k].order === currentOrder - 1);
        if (otherWidget) {
          widgets[otherWidget] = { ...widgets[otherWidget], order: currentOrder };
          widgets[widgetId] = { ...widgets[widgetId], order: currentOrder - 1 };
        }
      }
      return widgets;
    });
  };

  const moveWidgetDown = (widgetId) => {
    setWidgetConfig(prev => {
      const widgets = { ...prev };
      const currentOrder = widgets[widgetId].order;
      const maxOrder = Object.keys(widgets).length - 1;
      if (currentOrder < maxOrder) {
        const otherWidget = Object.keys(widgets).find(k => widgets[k].order === currentOrder + 1);
        if (otherWidget) {
          widgets[otherWidget] = { ...widgets[otherWidget], order: currentOrder };
          widgets[widgetId] = { ...widgets[widgetId], order: currentOrder + 1 };
        }
      }
      return widgets;
    });
  };

  const resetWidgets = () => {
    setWidgetConfig(DEFAULT_WIDGETS);
    toast.success('Widget layout reset to default');
  };

  const getVisibleWidgets = () => {
    return Object.entries(widgetConfig)
      .filter(([_, config]) => config.visible)
      .sort((a, b) => a[1].order - b[1].order)
      .map(([id]) => id);
  };

  const isAdmin = ['admin', 'hr_manager'].includes(user.role);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome Banner */}
      <div 
        className="relative rounded-2xl overflow-hidden bg-cover bg-center h-48"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/7652178/pexels-photo-7652178.jpeg)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-transparent" />
        <div className="relative h-full flex flex-col justify-center px-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            {t('dashboard.welcome')}, {user.full_name}!
          </h1>
          <p className="text-slate-200 text-lg">
            {t('dashboard.subtitle')}
          </p>
        </div>
        {/* Widget Settings Button */}
        {isAdmin && (
          <Button
            onClick={() => setShowWidgetSettings(true)}
            variant="ghost"
            size="sm"
            className="absolute top-4 right-4 text-white/70 hover:text-white hover:bg-white/10"
            data-testid="widget-settings-btn"
          >
            <Settings className="h-4 w-4 mr-1" />
            Customize
          </Button>
        )}
      </div>

      {/* Widget Settings Dialog */}
      <Dialog open={showWidgetSettings} onOpenChange={setShowWidgetSettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Customize Dashboard Widgets</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {Object.entries(widgetConfig)
              .sort((a, b) => a[1].order - b[1].order)
              .map(([widgetId, config]) => (
                <div
                  key={widgetId}
                  className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <GripVertical className="h-4 w-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {config.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveWidgetUp(widgetId)}
                      className="h-8 w-8 p-0"
                    >
                      ↑
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveWidgetDown(widgetId)}
                      className="h-8 w-8 p-0"
                    >
                      ↓
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleWidget(widgetId)}
                      className={`h-8 w-8 p-0 ${config.visible ? 'text-emerald-600' : 'text-slate-400'}`}
                    >
                      {config.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              ))}
          </div>
          <div className="flex justify-between">
            <Button variant="outline" size="sm" onClick={resetWidgets}>
              Reset to Default
            </Button>
            <Button size="sm" onClick={() => setShowWidgetSettings(false)}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats Grid */}
      {isAdmin ? (
        <>
          {getVisibleWidgets().map((widgetId) => {
            // Iter49 (Ankur 23-Jun-2026) — replace hardcoded "+12%" with the
            // real Month-over-Month growth derived from `employee_growth`.
            // When current or previous month is 0, suppress the trend chip so
            // the dashboard stops showing nonsense like "+12% growth on 0".
            const empGrowth = stats?.employee_growth || [];
            let employeeTrend = null;
            if (empGrowth.length >= 2) {
              const curr = Number(empGrowth[empGrowth.length - 1]?.employees ?? 0);
              const prev = Number(empGrowth[empGrowth.length - 2]?.employees ?? 0);
              if (prev > 0 && curr > 0) {
                const delta = ((curr - prev) / prev) * 100;
                employeeTrend = `${delta >= 0 ? '+' : ''}${delta.toFixed(0)}%`;
              } else if (prev === 0 && curr > 0) {
                employeeTrend = `+${curr} new`;
              }
              // else: keep null → no chip shown
            }
            switch (widgetId) {
              case 'stats':
                return (
                  <div key="stats" className="grid grid-cols-1 md:grid-cols-4 gap-6" data-testid="stats-widget">
                    <StatCard
                      title={t('dashboard.totalEmployees')}
                      value={stats?.total_employees || 0}
                      icon={Users}
                      color="bg-blue-500"
                      trend={employeeTrend}
                      onClick={() => navigate('/dashboard/employees')}
                    />
                    <StatCard
                      title={t('dashboard.pendingLeaves')}
                      value={stats?.pending_leaves || 0}
                      icon={Calendar}
                      color="bg-amber-500"
                      alert={stats?.pending_leaves > 0}
                      onClick={() => navigate('/dashboard/leave')}
                    />
                    <StatCard
                      title={t('dashboard.pendingClaims')}
                      value={stats?.pending_claims || 0}
                      icon={FileText}
                      color="bg-purple-500"
                      alert={stats?.pending_claims > 0}
                      onClick={() => navigate('/dashboard/claims')}
                    />
                    <StatCard
                      title={t('dashboard.monthlyPayroll')}
                      value={`RM ${(stats?.current_month_payroll?.total_net || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`}
                      icon={Banknote}
                      color="bg-emerald-500"
                      onClick={() => navigate('/dashboard/payroll')}
                    />
                  </div>
                );
              
              case 'aiUsage':
                // AI Usage Widget - Only renders for admin/owner
                return (
                  <Suspense key="aiUsage" fallback={<div className="h-32 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse" />}>
                    <AIUsageWidget />
                  </Suspense>
                );
              
              case 'quickActions':
                return (
                  <div key="quickActions" className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="quick-actions-widget">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">{t('dashboard.quickActions')}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <QuickActionCard title={t('dashboard.addEmployee')} onClick={() => navigate('/dashboard/employees?add=1')} icon={Users} />
                      <QuickActionCard title={t('dashboard.processPayroll')} href="/dashboard/payroll" icon={Banknote} />
                      <QuickActionCard title={t('dashboard.reviewLeaves')} href="/dashboard/leave" icon={Calendar} />
                      <QuickActionCard title={t('dashboard.viewReports')} href="/dashboard/reports" icon={TrendingUp} />
                    </div>
                  </div>
                );
              
              // TC-DASH-007: Recent Activities Widget
              case 'recentActivities':
                return (
                  <div key="recentActivities" className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="recent-activities-widget">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Recent Activities</h3>
                    <div className="space-y-3">
                      {recentActivities.length > 0 ? recentActivities.map((activity, idx) => {
                        // Ankur 25-Jun-2026: surface module type so testers
                        // can tell at a glance which module each row came
                        // from (the previous widget hid this and every row
                        // looked like a leave event).
                        const typeBadge = {
                          leave: { label: 'Leave', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
                          claim: { label: 'Claim', cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
                          overtime: { label: 'Overtime', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
                          payroll: { label: 'Payroll', cls: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300' },
                          employee: { label: 'Employee', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
                          adjustment: { label: 'Adjustment', cls: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300' },
                          document: { label: 'Document', cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' },
                        }[activity.type] || { label: (activity.type || 'Activity').toString(), cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200' };
                        const statusDot =
                          activity.status === 'approved' || activity.status === 'completed' || activity.status === 'finalized' ? 'bg-emerald-500' :
                          activity.status === 'pending' || activity.status === 'draft' || activity.status === 'sent' ? 'bg-amber-500' :
                          activity.status === 'rejected' || activity.status === 'cancelled' ? 'bg-red-500' :
                          activity.status === 'applied' || activity.status === 'processed' ? 'bg-teal-500' : 'bg-slate-400';
                        return (
                          <div key={`${activity.type}-${activity.ref_id || idx}`} className="flex items-start space-x-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50" data-testid={`recent-activity-${activity.type}`}>
                            <div className={`w-2 h-2 mt-2 rounded-full ${statusDot}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${typeBadge.cls}`}>
                                  {typeBadge.label}
                                </span>
                                <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{activity.title}</p>
                              </div>
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{activity.description}</p>
                              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                                {activity.time ? new Date(activity.time).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                              </p>
                            </div>
                          </div>
                        );
                      }) : (
                        <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">No recent activities</p>
                      )}
                    </div>
                  </div>
                );

              case 'employeeGrowth':
                return (
                  <div key="employeeGrowth" className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="employee-growth-widget">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Employee Growth</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={stats?.employee_growth || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="month" stroke="#64748b" />
                        <YAxis stroke="#64748b" domain={[0, 'auto']} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                          labelStyle={{ color: '#94a3b8' }}
                          itemStyle={{ color: '#10b981' }}
                        />
                        <Line type="monotone" dataKey="employees" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                );
              
              // TC-DASH-009: Attendance Chart Widget
              case 'attendanceChart':
                return (
                  <div key="attendanceChart" className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="attendance-chart-widget">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Attendance Overview (Last 7 Days)</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={attendanceData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="day" stroke="#64748b" />
                        <YAxis stroke="#64748b" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                          labelStyle={{ color: '#94a3b8' }}
                        />
                        <Bar dataKey="present" fill="#10b981" name="Present" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="late" fill="#f59e0b" name="Late" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="absent" fill="#ef4444" name="Absent" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );

              // TC-DASH-010: Leave Distribution Widget
              case 'leaveDistribution':
                return (
                  <div key="leaveDistribution" className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="leave-distribution-widget">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Leave Distribution</h3>
                    {leaveDistribution.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={leaveDistribution} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" stroke="#64748b" />
                          <YAxis dataKey="name" type="category" stroke="#64748b" width={80} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                            labelStyle={{ color: '#94a3b8' }}
                          />
                          <Bar dataKey="value" fill="#6366f1" name="Requests" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">No leave data available</p>
                    )}
                  </div>
                );

              case 'departmentDist':
                return (
                  <div key="departmentDist" className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="department-dist-widget">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Department Distribution</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={stats?.department_distribution || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="dept" stroke="#64748b" tick={{ fontSize: 12 }} />
                        <YAxis stroke="#64748b" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                          labelStyle={{ color: '#94a3b8' }}
                          itemStyle={{ color: '#10b981' }}
                        />
                        <Bar dataKey="count" fill="#10b981" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              
              case 'holidays':
                return publicHolidays.length > 0 ? (
                  <div key="holidays" className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="holidays-widget">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Upcoming Holidays</h3>
                    <div className="space-y-3">
                      {publicHolidays.map((holiday, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-900 dark:text-slate-100">{holiday.name}</p>
                            <p className="text-sm text-slate-500">{new Date(holiday.date).toLocaleDateString('en-MY', { weekday: 'long', day: 'numeric', month: 'short' })}</p>
                          </div>
                          <Calendar className="h-5 w-5 text-emerald-500" />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              
              default:
                return null;
            }
          })}
          {/* Surface pending signature requests for HR/Admin too — they may
              be a signer on Director-approval docs and shouldn't miss them. */}
          <Suspense fallback={null}>
            <PendingSignaturesWidget />
          </Suspense>
        </>
      ) : (
        <>
          {/* Employee View */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Annual Leave Balance</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="emp-leave-balance">
                    {empStats.leave_balance_days === null ? '—' : `${empStats.leave_balance_days} Days`}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">This Month</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="emp-attendance-days">
                    {empStats.attendance_days_this_month === null ? '—' : `${empStats.attendance_days_this_month} Days`}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Pending Claims</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="emp-pending-claims">
                    {empStats.pending_claims === null ? '—' : empStats.pending_claims}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Iter88t — Per-leave-type breakdown widget */}
          {leaveBreakdown.length > 0 && (
            <div
              data-testid="emp-leave-breakdown-card"
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center">
                  <Calendar className="h-5 w-5 text-emerald-600 mr-2" />
                  Leave Balance Breakdown
                </h3>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  Year: {new Date().getFullYear()}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-700">
                      <th className="py-2 pr-4">Leave Type</th>
                      <th className="py-2 px-2 text-right">Entitlement</th>
                      {/* Iter54 (Ankur Bug D) — Carry Forward column added so
                          the dashboard table reconciles with the HR view
                          (which has always shown carried_forward). */}
                      <th className="py-2 px-2 text-right">Carry Forward</th>
                      <th className="py-2 px-2 text-right">Used</th>
                      <th className="py-2 px-2 text-right">Pending</th>
                      <th className="py-2 pl-2 text-right">Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveBreakdown.map((row) => {
                      const entitlement = Number(row.entitlement ?? row.default_days ?? 0);
                      const carried = Number(row.carried_forward ?? row.carry_forward ?? 0);
                      const used = Number(row.used ?? 0);
                      const pending = Number(row.pending ?? 0);
                      const available = Number(row.available ?? row.balance ?? 0);
                      const low = entitlement > 0 && available <= entitlement * 0.2;
                      return (
                        <tr
                          key={row.leave_type_id}
                          data-testid={`leave-breakdown-row-${row.leave_type_id}`}
                          className="border-b border-slate-50 dark:border-slate-700/40 last:border-0"
                        >
                          <td className="py-2 pr-4 font-medium text-slate-900 dark:text-slate-100">
                            {row.name}
                          </td>
                          <td className="py-2 px-2 text-right text-slate-700 dark:text-slate-300">
                            {entitlement}
                          </td>
                          <td className="py-2 px-2 text-right text-slate-700 dark:text-slate-300">
                            {carried > 0 ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                +{carried}
                              </span>
                            ) : (
                              <span className="text-slate-400">0</span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-right text-slate-700 dark:text-slate-300">
                            {used}
                          </td>
                          <td className="py-2 px-2 text-right">
                            {pending > 0 ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                {pending}
                              </span>
                            ) : (
                              <span className="text-slate-400">0</span>
                            )}
                          </td>
                          <td className={`py-2 pl-2 text-right font-semibold ${low ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                            {available}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
                <strong>Available</strong> = Entitlement + Carried Forward − Used (approved). <strong>Pending</strong> counts requests still awaiting HR/Manager approval and is not deducted yet.
              </p>
            </div>
          )}

          {/* Awaiting Your Signature — surfaces pending docs at the top so
              employees can't miss a signature request even if they ignore
              the notification bell. */}
          <Suspense fallback={null}>
            <PendingSignaturesWidget />
          </Suspense>

          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <QuickActionCard title="Apply Leave" href="/dashboard/leave" icon={Calendar} />
              <QuickActionCard title="Clock In/Out" href="/dashboard/attendance" icon={Clock} />
              <QuickActionCard title="Submit Claim" href="/dashboard/claims" icon={FileText} />
              <QuickActionCard title="My Payslips" href="/dashboard/payslips" icon={Banknote} />
            </div>
          </div>

          {/* Public Holidays Widget */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center">
              <Calendar className="h-5 w-5 text-emerald-600 mr-2" />
              Upcoming Public Holidays
            </h3>
            {publicHolidays.length > 0 ? (
              <div className="space-y-3">
                {publicHolidays.map((holiday, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-xl">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-slate-100">{holiday.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {new Date(holiday.date).toLocaleDateString('en-MY', { 
                          weekday: 'long',
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </p>
                    </div>
                    <div className="h-10 w-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 dark:text-slate-400 text-center py-4">No upcoming public holidays</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color, trend, alert, onClick }) {
  return (
    <div 
      onClick={onClick}
      className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm card-hover cursor-pointer transition-all hover:shadow-md hover:border-slate-300 dark:hover:border-slate-500" 
      data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`h-12 w-12 rounded-xl ${color} bg-opacity-10 flex items-center justify-center`}>
          <Icon className={`h-6 w-6 ${color.replace('bg-', 'text-')}`} />
        </div>
        {alert && (
          <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="h-4 w-4 text-red-600" />
          </div>
        )}
      </div>
      <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">{title}</h3>
      <div className="flex items-end justify-between">
        <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">{value}</p>
        {trend && (
          <span className="text-xs font-medium text-emerald-600">{trend}</span>
        )}
      </div>
    </div>
  );
}

function QuickActionCard({ title, href, onClick, icon: Icon }) {
  const navigate = useNavigate();
  
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (href) {
      navigate(href);
    }
  };
  
  return (
    <button
      onClick={handleClick}
      data-testid={`quick-action-${title.toLowerCase().replace(/\s+/g, '-')}`}
      className="flex flex-col items-center justify-center p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all duration-200 group"
    >
      <Icon className="h-8 w-8 text-slate-400 dark:text-slate-500 group-hover:text-emerald-600 mb-2 transition-colors" />
      <span className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-emerald-900 dark:group-hover:text-emerald-400 text-center">{title}</span>
    </button>
  );
}